import { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, apiGet } from '../api/client';
import {
  fetchProviders,
  forgotPassword,
  loginTelegramWidget,
  phoneStart,
  phoneVerify,
  type ProvidersStatus,
  type TelegramWidgetUser,
} from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { useAuthDialog } from '../auth/AuthDialogContext';
import { formatAzNational, isValidAzMobile, nationalDigitsFromInput, toE164 } from '../auth/azPhone';
import { beginGoogle } from '../auth/google';
import { beginTelegramOidc } from '../auth/telegramOidc';
import { selectableCampaigns, type ListedCampaign } from '../auth/welcomeCampaign';
import { authErrorMessage } from '../auth/errors';
import { getStoredReferralCode } from '../lib/referralCapture';
import { isTelegramMiniApp } from '../telegram/webapp';
import { GoogleMark, MailMark, PhoneMark, TelegramMark } from './brandIcons';
import { TelegramLoginButton } from './TelegramLoginButton';

type Channel = 'email' | 'phone';

export function AuthModal() {
  const { t } = useTranslation(['auth', 'nav', 'common', 'errors']);
  const { user, login, register, applyUser } = useAuth();
  const { open, mode, closeAuth, finishSignedIn, setMode } = useAuthDialog();
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const [channel, setChannel] = useState<Channel>('email');
  const [email, setEmail] = useState('');
  const [phoneNational, setPhoneNational] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [bonusCode, setBonusCode] = useState('');
  const [bonusOpen, setBonusOpen] = useState(false);
  const [campaigns, setCampaigns] = useState<ListedCampaign[]>([]);
  const [providers, setProviders] = useState<ProvidersStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState<{ delivery: boolean } | null>(null);
  const [phoneChallenge, setPhoneChallenge] = useState<{ e164: string; masked: string; resendAt: number } | null>(
    null,
  );
  const [otpCode, setOtpCode] = useState('');
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const id = window.setTimeout(() => {
      if (channel === 'phone') phoneRef.current?.focus();
      else emailRef.current?.focus();
    }, 40);
    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(id);
    };
  }, [open, mode, channel]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') closeAuth();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeAuth]);

  useEffect(() => {
    setError(null);
    setForgotSent(null);
    setChannel('email');
    setSubmitting(false);
    setBonusOpen(false);
    setPhoneChallenge(null);
    setOtpCode('');
  }, [mode]);

  useEffect(() => {
    if (!phoneChallenge) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [phoneChallenge]);

  useEffect(() => {
    if (providers && !providers.sms.enabled) {
      setChannel('email');
      setPhoneChallenge(null);
      setOtpCode('');
    }
  }, [providers]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fetchProviders()
      .then((status) => {
        if (!cancelled) setProviders(status);
      })
      .catch(() => {
        if (!cancelled) setProviders(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'register') return;
    let cancelled = false;
    void apiGet<ListedCampaign[]>('/api/v1/bonuses/campaigns')
      .then((rows) => {
        if (!cancelled) setCampaigns(selectableCampaigns(rows));
      })
      .catch(() => {
        if (!cancelled) setCampaigns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mode]);

  useEffect(() => {
    if (!open || !user) return;
    if (mode === 'register') finishSignedIn();
    else closeAuth();
  }, [open, user, mode, closeAuth, finishSignedIn]);

  if (!open || user) return null;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setError(null);

    if (channel === 'phone' && mode !== 'forgot') {
      if (phoneChallenge) {
        if (otpCode.replace(/\D/g, '').length !== 6) {
          setError(t('auth:errors.otp_invalid'));
          return;
        }
        if (!providers?.sms.enabled) {
          setError(t('auth:errors.sms_disabled'));
          return;
        }
        setSubmitting(true);
        try {
          const res = await phoneVerify(phoneChallenge.e164, otpCode.replace(/\D/g, ''), 'login');
          if (res.session) {
            applyUser(res.session.user);
            finishSignedIn();
          }
        } catch (err) {
          setError(authErrorMessage(t, err));
        } finally {
          setSubmitting(false);
        }
        return;
      }
      if (!isValidAzMobile(phoneNational)) {
        setError(t('auth:phoneInvalidAz'));
        return;
      }
      if (mode === 'register' && !acceptTerms) {
        setError(t('auth:acceptTermsRequired'));
        return;
      }
      if (!providers?.sms.enabled) {
        setError(t('auth:errors.sms_disabled'));
        return;
      }
      const e164 = toE164(phoneNational);
      if (!e164) {
        setError(t('auth:phoneInvalidAz'));
        return;
      }
      setSubmitting(true);
      try {
        const res = await phoneStart(e164, 'login');
        setPhoneChallenge({ e164, masked: res.phone_masked, resendAt: Date.now() + res.resend_in * 1000 });
        setOtpCode('');
      } catch (err) {
        setError(authErrorMessage(t, err));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'register') {
        if (!acceptTerms) {
          setError(t('auth:acceptTermsRequired'));
          return;
        }
        await register(email.trim(), password, acceptTerms);
        finishSignedIn();
      } else if (mode === 'login') {
        await login(email.trim(), password);
        closeAuth();
      } else {
        const res = await forgotPassword(email.trim());
        setForgotSent({ delivery: res.delivery_available });
      }
    } catch (err) {
      if (mode === 'login' && err instanceof ApiError && err.status === 401) {
        setError(t('auth:errors.invalid_credentials'));
      } else {
        setError(authErrorMessage(t, err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onGoogle() {
    if (!providers?.google.enabled || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await beginGoogle('login');
    } catch (err) {
      setError(authErrorMessage(t, err));
      setSubmitting(false);
    }
  }

  async function onTelegramOidc() {
    if (!providers?.telegram.oidc_enabled || submitting) return;
    if (mode === 'register' && !acceptTerms) {
      setError(t('auth:acceptTermsRequired'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await beginTelegramOidc('login');
    } catch (err) {
      setError(authErrorMessage(t, err));
      setSubmitting(false);
    }
  }

  async function onTelegramAuth(tgUser: TelegramWidgetUser) {
    if (providers?.telegram.oidc_enabled) return;
    if (!providers?.telegram.widget_enabled || !providers.telegram.bot_username) return;
    if (mode === 'register' && !acceptTerms) {
      setError(t('auth:acceptTermsRequired'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await loginTelegramWidget(tgUser, getStoredReferralCode());
      applyUser(res.user);
      finishSignedIn();
    } catch (err) {
      setError(authErrorMessage(t, err));
      setSubmitting(false);
    }
  }

  async function resendPhoneCode() {
    if (!phoneChallenge || !providers?.sms.enabled || submitting) return;
    if (phoneChallenge.resendAt > Date.now()) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await phoneStart(phoneChallenge.e164, 'login');
      setPhoneChallenge({ e164: phoneChallenge.e164, masked: res.phone_masked, resendAt: Date.now() + res.resend_in * 1000 });
      setOtpCode('');
    } catch (err) {
      setError(authErrorMessage(t, err));
    } finally {
      setSubmitting(false);
    }
  }

  const title =
    mode === 'register' ? t('auth:registerTitle') : mode === 'forgot' ? t('auth:forgotTitle') : t('auth:loginTitle');
  const selectedBonus = campaigns.find((row) => row.code === bonusCode);
  const googleOn = Boolean(providers?.google.enabled);
  const phoneAuthOn = Boolean(providers?.sms.enabled);
  const telegramOidcOn = Boolean(providers?.telegram.oidc_enabled);
  const telegramClassicOn = Boolean(
    !telegramOidcOn && providers?.telegram.widget_enabled && providers.telegram.bot_username,
  );
  const showTelegram = (telegramOidcOn || telegramClassicOn) && !isTelegramMiniApp() && mode !== 'forgot' && channel === 'email';
  const showGoogle = googleOn && mode !== 'forgot';

  return (
    <div className="auth-modal-root" role="presentation" onMouseDown={closeAuth}>
      <div
        className="auth-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={cardRef}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="auth-modal-head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="auth-modal-close" onClick={closeAuth} aria-label={t('auth:closeDialog')}>
            ×
          </button>
        </header>

        {mode !== 'forgot' && phoneAuthOn ? (
          <div className="auth-modal-tabs" role="tablist" aria-label={title}>
            <button
              type="button"
              role="tab"
              aria-selected={channel === 'email'}
              className={channel === 'email' ? 'is-active' : ''}
              onClick={() => {
                setChannel('email');
                setPhoneChallenge(null);
                setOtpCode('');
                setError(null);
              }}
            >
              <MailMark />
              {t('auth:tabEmail')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={channel === 'phone'}
              className={channel === 'phone' ? 'is-active' : ''}
              onClick={() => {
                setChannel('phone');
                setError(null);
              }}
            >
              <PhoneMark />
              {t('auth:tabPhone')}
            </button>
          </div>
        ) : null}

        <form className="auth-modal-body" onSubmit={(event) => void onSubmit(event)}>
          {channel === 'phone' && mode !== 'forgot' && phoneChallenge ? (
            <>
              <p className="auth-modal-hint">{t('auth:codeSentTo', { phone: phoneChallenge.masked })}</p>
              <label className="auth-modal-field">
                <PhoneMark />
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder={t('auth:codeLabel')}
                  aria-label={t('auth:codeLabel')}
                  value={otpCode}
                  onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </label>
              <div className="auth-modal-otp-row">
                <button
                  type="button"
                  className="auth-modal-textlink"
                  disabled={submitting || phoneChallenge.resendAt > now}
                  onClick={() => void resendPhoneCode()}
                >
                  {phoneChallenge.resendAt > now
                    ? t('auth:resendIn', { seconds: Math.max(0, Math.ceil((phoneChallenge.resendAt - now) / 1000)) })
                    : t('auth:resendCode')}
                </button>
                <button
                  type="button"
                  className="auth-modal-textlink"
                  onClick={() => {
                    setPhoneChallenge(null);
                    setOtpCode('');
                    setError(null);
                  }}
                >
                  {t('auth:changeNumber')}
                </button>
              </div>
            </>
          ) : channel === 'phone' && mode !== 'forgot' ? (
            <label className="auth-modal-field">
              <PhoneMark />
              <span className="auth-modal-prefix">+994</span>
              <input
                ref={phoneRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9 ]*"
                placeholder={t('auth:phonePlaceholder')}
                autoComplete="tel-national"
                aria-label={t('auth:phoneLabel')}
                value={formatAzNational(phoneNational)}
                onChange={(event) => setPhoneNational(nationalDigitsFromInput(event.target.value))}
              />
            </label>
          ) : (
            <label className="auth-modal-field">
              <MailMark />
              <input
                ref={emailRef}
                type="email"
                autoComplete="username"
                placeholder={t('auth:emailPlaceholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                maxLength={255}
                required={channel === 'email'}
              />
            </label>
          )}

          {mode !== 'forgot' && channel === 'email' ? (
            <label className="auth-modal-field">
              <LockIcon />
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                placeholder={t('auth:password')}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={mode === 'register' ? 8 : undefined}
                maxLength={128}
                required
              />
              <button
                type="button"
                className="auth-modal-eye"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? t('auth:hidePassword') : t('auth:showPassword')}
              >
                {showPassword ? t('auth:hidePassword') : t('auth:showPassword')}
              </button>
            </label>
          ) : null}

          {mode === 'register' && !phoneChallenge ? (
            <>
              <div className={`auth-modal-bonus${bonusOpen ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="auth-modal-bonus-trigger"
                  aria-expanded={bonusOpen}
                  aria-haspopup="listbox"
                  onClick={() => setBonusOpen((value) => !value)}
                >
                  <GiftIcon />
                  <span>{selectedBonus?.title || t('auth:casinoBonus')}</span>
                </button>
                {bonusOpen ? (
                  <ul className="auth-modal-bonus-menu" role="listbox" aria-label={t('auth:casinoBonus')}>
                    {campaigns.length === 0 ? (
                      <li className="is-empty">{t('auth:casinoBonusInactive')}</li>
                    ) : (
                      campaigns.map((row) => (
                        <li key={row.code}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={bonusCode === row.code}
                            onClick={() => {
                              setBonusCode(row.code);
                              setBonusOpen(false);
                            }}
                          >
                            {row.title || row.code}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                ) : null}
              </div>

              <button
                type="button"
                className={`auth-modal-promo${promoOpen ? ' is-open' : ''}`}
                onClick={() => {
                  setBonusOpen(false);
                  setPromoOpen((value) => !value);
                }}
              >
                <TicketIcon />
                {t('auth:promoToggle')}
              </button>
              {promoOpen ? (
                <label className="auth-modal-field">
                  <TicketIcon />
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                    placeholder={t('auth:promoPlaceholder')}
                    maxLength={64}
                    autoComplete="off"
                  />
                </label>
              ) : null}

              <label className="auth-modal-check">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(event) => setAcceptTerms(event.target.checked)}
                  required={channel === 'email'}
                />
                <span>
                  {t('auth:acceptTermsPrefix')}
                  <Link
                    className="auth-modal-terms-text"
                    to="/qaydalar/terms"
                    onClick={() => closeAuth()}
                  >
                    {t('auth:acceptTermsLink')}
                  </Link>
                  {t('auth:acceptTermsSuffix')}
                </span>
              </label>
            </>
          ) : null}

          {mode === 'login' ? (
            <button type="button" className="auth-modal-textlink" onClick={() => setMode('forgot')}>
              {t('auth:resetPassword')}
            </button>
          ) : null}

          {mode === 'forgot' ? (
            <>
              <p className="auth-modal-hint">{t('auth:forgotIntro')}</p>
              {forgotSent ? (
                <p className="auth-modal-note" role="status">
                  {t('auth:forgotSent')}
                  {!forgotSent.delivery ? ` ${t('auth:emailDeliveryOff')}` : ''}
                </p>
              ) : null}
            </>
          ) : null}

          {error ? (
            <p className="auth-modal-error" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="auth-modal-submit" disabled={submitting}>
            {submitting
              ? t('common:loading')
              : phoneChallenge
                ? t('auth:verifyCode')
                : channel === 'phone' && mode !== 'forgot' && providers?.sms.enabled
                  ? t('auth:sendCode')
                  : mode === 'register'
                    ? t('auth:registerSubmit')
                    : mode === 'forgot'
                      ? t('auth:forgotCta')
                      : t('auth:loginSubmit')}
          </button>
        </form>

        {showTelegram || showGoogle ? (
          <div className="auth-modal-social" aria-label={t('auth:socialMethods')}>
            {showTelegram ? (
              <div className="auth-modal-telegram">
                <p className="auth-modal-or">{t('auth:orContinue')}</p>
                {telegramOidcOn ? (
                  <button
                    type="button"
                    className="auth-modal-telegram-btn"
                    disabled={submitting}
                    onClick={() => void onTelegramOidc()}
                  >
                    <TelegramMark size={20} />
                    {t('auth:continueWithTelegram')}
                  </button>
                ) : providers?.telegram.bot_username ? (
                  <TelegramLoginButton
                    botUsername={providers.telegram.bot_username}
                    size="large"
                    onAuth={(tgUser) => void onTelegramAuth(tgUser)}
                  />
                ) : null}
              </div>
            ) : null}
            {showGoogle ? (
              <button
                type="button"
                className="auth-modal-social-item"
                aria-label={t('auth:googleReady')}
                disabled={submitting}
                onClick={() => void onGoogle()}
              >
                <GoogleMark size={20} />
              </button>
            ) : null}
          </div>
        ) : null}

        <footer className="auth-modal-foot">
          {mode === 'register' ? (
            <p>
              {t('auth:haveAccountPrompt')}{' '}
              <button type="button" onClick={() => setMode('login')}>
                {t('auth:haveAccountAction')}
              </button>
            </p>
          ) : mode === 'forgot' ? (
            <p>
              <button type="button" onClick={() => setMode('login')}>
                {t('auth:backToLogin')}
              </button>
            </p>
          ) : (
            <p>
              {t('auth:needAccountPrompt')}{' '}
              <button type="button" onClick={() => setMode('register')}>
                {t('auth:needAccountAction')}
              </button>
            </p>
          )}
        </footer>
      </div>
    </div>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="10" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 10V8a4 4 0 0 1 8 0v2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function GiftIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="4" y="8" width="16" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8v12M4 12h16M9 8c0-2 1.2-3 3-3s3 1 3 3" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function TicketIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 8h16v3a2 2 0 0 1 0 4v3H4v-3a2 2 0 0 1 0-4z" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}
